import type { CommentsProvider } from "@framework/comments/comments.types";
import {
  resolveCommentProvider,
  type ResolvedCommentProvider,
} from "@framework/comments/resolve-comment-provider";
import type {
  CommentLocation,
  CommentsConfig,
} from "@voxmedia/duet-context/context/config-context";

export interface ResolveCommentsConfigArgs {
  isMobileApp?: boolean;
  /** Per-community override (SB Nation communities-metadata). */
  commentsProvider?: CommentsProvider;
  /**
   * Env-derived default (`NEXT_PUBLIC_CYCLONE_COMMENTS_ENABLED` via
   * `cycloneConfig.commentsEnabled`). When true, cyclone is the default and
   * per-community metadata can override back to coral.
   */
  cycloneCommentsDefault?: boolean;
  /** Per-community embed override (SB Nation communities-metadata). */
  commentLocation?: CommentLocation;
  /** Brand default when commentLocation is not set per community. */
  defaultLocation?: CommentLocation;
}

/**
 * Resolves provider + embed location for the active community.
 * SB Nation passes metadata fields; every brand passes `cycloneCommentsDefault`
 * plus `defaultLocation`.
 */
export function resolveCommentsConfig(
  args: ResolveCommentsConfigArgs,
): CommentsConfig {
  const provider = resolveCommentProvider({
    isMobileApp: args.isMobileApp,
    commentsProvider: args.commentsProvider,
    cycloneCommentsDefault: args.cycloneCommentsDefault,
  });

  const location = resolveCommentLocation({
    provider,
    commentLocation: args.commentLocation,
    defaultLocation: args.defaultLocation,
  });

  return { provider, location };
}

function resolveCommentLocation(args: {
  provider: ResolvedCommentProvider;
  commentLocation?: CommentLocation;
  defaultLocation?: CommentLocation;
}): CommentLocation {
  if (args.commentLocation) {
    return args.commentLocation;
  }

  // Cyclone and noop use inline body embeds; Coral uses the brand default.
  if (args.provider === "cyclone" || args.provider === "noop") {
    return "body";
  }

  return args.defaultLocation ?? "drawer";
}
